import {Component, computed, effect, inject, input, output, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {Dialog} from 'primeng/dialog';
import {SharedModule} from 'primeng/api';
import {SelectButton} from 'primeng/selectbutton';
import {DirectusTranslationPipe} from '@frontend/directus';
import {
  SubCriteriaRow,
  ProviderColumn,
} from '../../table/provider-results-table/provider-results-table.types';
import {SubcriteriaAttributeScoresTableComponent} from '../../subcriteria-attribute-scores-modal/subcriteria-attribute-scores-table.component';
import {SubcriteriaAttributeScoresProviderFilterComponent} from '../../subcriteria-attribute-scores-modal/subcriteria-attribute-scores-provider-filter.component';
import {SubcriteriaAttributeScoreData} from '../../subcriteria-attribute-scores-modal/subcriteria-attribute-scores.types';
import {SubcriteriaAttributeScoresHeatmapComponent} from '../../subcriteria-attribute-scores-modal/subcriteria-attribute-scores-heatmap.component';
import {
  EnrichedEvaluationDataResponse,
  EvaluationAgendaItemVm,
} from '../../../../pages/customer/project-sequence/chapter/chapter-7-rfd/step-7-5-provider-evaluation/evaluation-models';
import {RfdProviderScore} from '../../../services/rfd-score-calculation.service';

@Component({
  selector: 'app-rfd-agenda-item-scores-modal',
  imports: [
    FormsModule,
    TranslocoPipe,
    Dialog,
    SharedModule,
    SelectButton,
    SubcriteriaAttributeScoresTableComponent,
    SubcriteriaAttributeScoresProviderFilterComponent,
    SubcriteriaAttributeScoresHeatmapComponent,
  ],
  templateUrl: './rfd-agenda-item-scores-modal.component.html',
})
export class RfdAgendaItemScoresModalComponent {
  private readonly translocoService = inject(TranslocoService);
  private readonly directusTranslationPipe = new DirectusTranslationPipe();

  readonly visible = input.required<boolean>();
  readonly subCriteria = input<SubCriteriaRow | null>(null);
  readonly providerColumns = input<ProviderColumn[]>([]);
  readonly isExponentialScale = input<boolean>(false);
  readonly rfdEvaluationData = input<EnrichedEvaluationDataResponse | null>(null);
  readonly rfdScoresByToolId = input<Map<string, RfdProviderScore>>(new Map());

  readonly visibleChange = output<boolean>();

  private readonly selectedProviderIds = signal<string[]>([]);
  protected readonly selectedProviderIdsList = computed(() => this.selectedProviderIds());
  protected readonly viewMode = signal<'table' | 'chart'>('table');
  protected readonly hideImportanceColumn = true;

  protected readonly viewOptions = [
    {label: 'Table', value: 'table', translationKey: 'project.steps.viewTable'},
    {label: 'Heatmap', value: 'chart', translationKey: 'project.steps.viewHeatmap'},
  ];

  protected readonly filteredProviders = computed(() => {
    const selected = new Set(this.selectedProviderIds());
    return this.providerColumns().filter((provider) => selected.has(provider.hrToolDirectusId));
  });

  protected readonly subcriteriaLabel = computed(() => this.subCriteria()?.translatedLabel);

  protected readonly agendaRows = computed<SubcriteriaAttributeScoreData[]>(() => {
    const rfdData = this.rfdEvaluationData();
    if (!rfdData) return [];

    const agendaItems = [...(rfdData.agendaItems ?? [])].sort(
      (a, b) => a.orderNumber - b.orderNumber,
    );
    const lang = this.translocoService.getActiveLang();

    return agendaItems.map((agendaItem) => {
      const providerValuesByHrToolId = new Map<string, number | null>();
      this.providerColumns().forEach((provider) => {
        const providerScore = this.rfdScoresByToolId().get(provider.hrToolDirectusId);
        providerValuesByHrToolId.set(
          provider.hrToolDirectusId,
          providerScore?.agendaScores.get(agendaItem.id) ?? null,
        );
      });

      const topicLabel = this.resolveAgendaTopic(agendaItem, lang);
      return {
        directusId: `agenda-${agendaItem.id}`,
        agendaItemId: agendaItem.id,
        scaNumber: agendaItem.orderNumber,
        rowCode: `AG ${agendaItem.orderNumber}`,
        label: topicLabel,
        fulfillmentType: null,
        customerAverageRating: null,
        importanceScore: null,
        importanceLabelTranslationKey: null,
        providerValuesByHrToolId,
      };
    });
  });

  constructor() {
    effect(() => {
      if (!this.visible()) return;
      const providerIds = this.providerColumns().map((provider) => provider.hrToolDirectusId);
      this.selectedProviderIds.set(providerIds);
    });
  }

  protected onSelectedProvidersChange(selectedIds: string[]): void {
    this.selectedProviderIds.set(selectedIds);
  }

  private resolveAgendaTopic(agendaItem: EvaluationAgendaItemVm, lang: string): string {
    return (
      this.directusTranslationPipe.transform(
        agendaItem.translations,
        lang,
        `Agenda ${agendaItem.orderNumber}`,
        'topic',
      ) ?? `Agenda ${agendaItem.orderNumber}`
    );
  }
}
